import { type GlyphKey } from '@kk/domain';

import { brand } from '../tokens';

/**
 * 24×24 stroke glyphs (Feather-style geometry, stroke 2, round caps/joins). Filled logos carry `fill: true`.
 * Add glyphs here only; screens never inline SVG.
 *
 * **BEFORE CONCLUDING THIS SET LACKS A GLYPH, READ THE ARTBOARD'S MARKUP.** Evan, 2026-09-17: *"the artboard
 * is the spec, and an inventory of what we happen to have is not."*
 *
 * The worked case, and it cost a wrong decision that was argued for at length. C26's stepper needed a minus.
 * This file had none, so the stepper was built with `+` and `−` set as TYPE, with a comment explaining that a
 * minus is a typographic character rather than a picture and that drawing one icon and setting the other would
 * be a mismatched pair. Every word of that was reasoning from the inventory. The board's own C26 markup had
 * `<path d="M5 12h14">` sitting in it, next to the bike and the ball that had likewise been called missing —
 * all three were lifted from the artboard in minutes once anyone looked there instead of here.
 *
 * So: a glyph absent from this file is a glyph nobody has lifted yet, not a glyph that does not exist.
 */
export interface Glyph {
  d: string | string[];
  fill?: boolean;
  /** Per-path fills for multicolour logos (same length as `d`). */
  fills?: readonly string[];
}

export const glyphs = {
  check: { d: 'M20 6L9 17l-5-5' },
  x: { d: 'M18 6L6 18M6 6l12 12' },
  plus: { d: 'M12 5v14M5 12h14' },
  /** C26's stepper takes the target down. The board draws it as a glyph, so it is one. */
  minus: { d: 'M5 12h14' },
  'arrow-left': { d: 'M19 12H5M12 19l-7-7 7-7' },
  /**
   * **THE ARROW POPPINS DOES NOT HAVE.** U+2192 is absent from every one of the eighteen Poppins faces this app
   * ships (verified against each `cmap`, 2026-09-21), so a literal "→" in a string renders from a system
   * fallback or as tofu — and CLAUDE.md says Poppins only. C03's promotion, P17's "68 → 72" and P14's
   * "72 → 69" all draw this instead. Mirrors `arrow-left` exactly, so the pair cannot drift.
   */
  'arrow-right': { d: 'M5 12h14M12 19l7-7-7-7' },
  /**
   * **THE TRIANGLE POPPINS DOES NOT HAVE.** U+25B2, same story, and it was shipping on the most-read surface in
   * the app: `deltaLabel` drew "▲ 4" on every kid card, C01, P16 and P17. Evan's rule that *"a delta's meaning
   * must survive greyscale — carried by a sign or a glyph"* depends on the glyph actually rendering.
   */
  'triangle-up': { d: 'M12 6l7 12H5z' },
  'chevron-right': { d: 'M9 18l6-6-6-6' },
  camera: {
    d: [
      'M22 18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3l2-3h6l2 3h3a2 2 0 0 1 2 2z',
      'M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    ],
  },
  bell: { d: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'] },
  eye: {
    d: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
  },
  'eye-off': {
    d: [
      'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94',
      'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19',
      'M14.12 14.12a3 3 0 1 1-4.24-4.24',
      'M1 1l22 22',
    ],
  },
  /**
   * C25's teal mark — "a grown-up said something to you, with a reason attached" (Evan, 2026-09-16). A speech
   * bubble rather than the board's information circle: a parent's note looks like a speech bubble everywhere
   * else in this app, and the rule covers a club staffer's denial as well as a parent's.
   */
  speech: {
    d: 'M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.8L3 21l1.9-5.1A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.4h.5a8.4 8.4 0 0 1 8 8v.4z',
  },
  /**
   * C25's FIFTH mark, for the two graduation cards and nothing else. Evan: *"they are the only two cards a
   * child will ever see that are not about a chore, a reward, or a grown-up… A child sees them twice in their
   * life. They should look like nothing else in the inbox."* An open door with an arrow leaving it — onward,
   * not a prize. Drawn on an ink disc in white; never magenta (rewards) and never sun (progress).
   */
  door: {
    d: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  },
  envelope: {
    d: [
      'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z',
      'M22 6l-10 7L2 6',
    ],
  },
  lock: {
    d: [
      'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z',
      'M7 11V7a5 5 0 0 1 10 0v4',
    ],
  },
  gift: {
    d: [
      'M2.5 8h19v4h-19z',
      'M4.5 12v9h15v-9',
      'M12 21V8C10 5 6.5 4 6.5 6.5C6.5 8 9.5 8 12 8C14.5 8 17.5 8 17.5 6.5C17.5 4 14 5 12 8',
    ],
  },
  flame: {
    d: 'M12 22c4.4 0 7-2.9 7-7 0-3.5-2.3-5.6-3.7-7.4-.4 1.7-1.4 2.6-2.3 3-.3-2.9-1.7-5.6-4-7.6.3 3.4-1.6 5-3 7.2C4.7 12.4 5 13.9 5 15c0 4.1 2.6 7 7 7z',
  },
  chart: { d: ['M3 3v18h18', 'M7 15l4-5 3 3 5-7'] },
  // New Build board 07-7: `<path d="M3 3v18h18"><path d="m7 9 4 4 3-3 5 6">`, the score going DOWN
  'chart-down': { d: ['M3 3v18h18', 'M7 9l4 4 3-3 5 6'] },
  // Feather `rotate-ccw`: the Cub tile's 2-minute Undo (DECISIONS 93.6)
  undo: { d: ['M1 4v6h6', 'M3.51 15a9 9 0 1 0 2.13-9.36L1 10'] },
  /**
   * P24d's refusal disc: an exclamation in a circle (board `<circle r="9">` + `M12 7.6v5.2M12 15.8h.01`). The
   * circle is a path, as `clock`'s is, because this set has no `<circle>` primitive.
   */
  alert: { d: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7.6v5.2', 'M12 15.8h.01'] },
  clock: { d: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'] },
  /** 04-1 / 04-2 / 04-4's pay-back day (New Build board, 9 Oct): a page, two rings joined along its top edge (three strokes, the validator's limit) and a rule. */
  calendar: {
    d: [
      'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
      'M8 2v2h8V2',
      'M3 10h18',
    ],
  },
  home: { d: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'] },
  list: { d: ['M9 6h12', 'M9 12h12', 'M9 18h12', 'M4 6h.01', 'M4 12h.01', 'M4 18h.01'] },
  star: {
    d: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z',
  },
  people: {
    d: [
      'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
      'M2 21v-1.5A5.5 5.5 0 0 1 7.5 14h3a5.5 5.5 0 0 1 5.5 5.5V21',
      'M16 3.5a3.5 3.5 0 0 1 0 7c3 0 6 2.5 6 6.5V21',
    ],
  },
  wallet: {
    d: ['M21 7H5a2 2 0 0 1 0-4h14v4', 'M21 7v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5', 'M16 13h.01'],
  },
  /**
   * C18 "First save" disc (`BadgeGlyph.name = 'card'`) — board `…dc.html:1312`
   * `<rect x=3 y=6 w=18 h=14 rx=3/><path d="M3 10h18"/>`, the rect written as a path.
   */
  card: {
    d: ['M6 6h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3z', 'M3 10h18'],
  },
  /**
   * The badge mark: C19 badge row tile (board `:1348`), C12 header pill and prize node (`:1195`, `:1206`).
   * Drawn two-tone on the board (`fill` + `stroke`); pass `Icon`'s `fill` to reproduce that.
   * Board: `<circle cx=12 cy=9 r=6/><path d="m8.5 14-1.5 7 5-3 5 3-1.5-7"/>`.
   */
  medal: { d: ['M6 9a6 6 0 1 0 12 0 6 6 0 1 0-12 0', 'm8.5 14-1.5 7 5-3 5 3-1.5-7'] },
  /** C13 / C14 read-aloud disc — board `:1214`, `:1232`. Inert this round (ruling 4): the control ships, the audio does not. */
  speaker: { d: ['M11 5 6 9H2v6h4l5 4z', 'M15.5 8.5a5 5 0 0 1 0 7'] },
  /** Outing / movie reward art: C04 tile plate (`:1032`), C05 type plate (`:1052`), C11 "Movie" plate (`:1172`). */
  ticket: {
    d: [
      'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2.5a2.5 2.5 0 0 0 0 5V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2.5a2.5 2.5 0 0 0 0-5z',
      'M15.5 5v14',
    ],
  },
  /** Biometric confirm: C05 "Mom or Dad, confirm here" (`:1056`) and the P13d confirm CTA (`:1580`). */
  'face-id': {
    d: [
      'M4 8V6a2 2 0 0 1 2-2h2',
      'M16 4h2a2 2 0 0 1 2 2v2',
      'M20 16v2a2 2 0 0 1-2 2h-2',
      'M8 20H6a2 2 0 0 1-2-2v-2',
      'M9 10v1M15 10v1M12 10v4h-1M9 16c1 1 5 1 6 0',
    ],
  },
  /** C15 medal centre, the "Saver" mark — board `:1252`: a money box with a coin over its slot. Drawn sun-filled, ink-stroked. */
  moneybox: {
    d: [
      'M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z',
      'M12 5V3M8 9h8',
    ],
  },
  /** C14 "New game" quiz art — board `:1236`: a handheld device, `<rect x=5 y=2 w=14 h=20 rx=3/><path d="M11 18h2"/>`. */
  game: {
    d: ['M8 2h8a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3z', 'M11 18h2'],
  },
  /** C14 "Dinner" quiz art — board `:1237`: a covered dish. */
  meal: { d: ['M4 11a8 8 0 0 1 16 0v1H4z', 'M2 12h20M6 12l1 6h10l1-6'] },
  /** C14 "Ice cream" quiz art (`:1238`) and the C11 ice-cream reward plate (`:1173`). */
  'ice-cream': { d: ['M8 14c-2 0-3-2-3-4a7 7 0 0 1 14 0c0 2-1 4-3 4', 'M9 14 12 22l3-8'] },
  /** C14 "Toy" quiz art (`:1239`) and the C04 item reward plate (`:1036`): three blocks. */
  /**
   * C26's "Bike" tile, lifted from the artboard's own inline SVG rather than drawn again — two wheels, a
   * frame and the handlebars. The board's `<circle cx cy r>` pairs become the two-arc path form this file
   * uses, exactly as `blocks` carries the artboard's `<rect>`s as rounded-rect paths.
   */
  bike: {
    d: [
      'M5.5 20a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
      'M18.5 20a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
      'M13.5 6.5h2L15 9l3.5 7.5L15 9H9l-3.5 7.5H12L15 9',
    ],
  },
  /** C26's "Ball" tile, from the same artboard: the outline and its two seams. */
  ball: {
    d: ['M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M12 3v18M3 12h18'],
  },
  blocks: {
    d: [
      'M4 9h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z',
      'M14 9h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z',
      'M9 3h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
    ],
  },
  /**
   * **C26's "Roblox" tile — the default first savings-goal choice.** Lifted from the redrawn C26 artboard
   * (2026-09-23), which says of these two: *"Two new pictures, gamepad and cube, drawn on the 24 grid with a
   * 2px stroke. Lift them into paths.ts from this artboard."*
   *
   * **The subject is a gamepad, never the game.** The board is explicit that *"game names are fine as a
   * family's own choices, but they stay out of store screenshots and marketing, and no game's logo is ever
   * drawn"* — so the NAME on the tile is the family's ("Roblox", editable on P31) and the PICTURE is a
   * generic controller. That separation is the reason this key is `gamepad` and not the product: the drawing
   * outlives whatever any one family types next to it.
   *
   * Distinct from `game`, which already existed for C06's "30 min on the Switch" reward — that one is a
   * handheld, this is the two-grip controller the artboard draws. Two subjects, two drawings, per this file's
   * one-drawing-per-subject rule.
   */
  gamepad: {
    d: [
      'M7 7h10a5 5 0 0 1 0 10h-1l-2-2h-4l-2 2H7A5 5 0 0 1 7 7z',
      'M8 10v4M6 12h4',
      'M15 11h.01M17 13h.01',
    ],
  },
  /** C26's "Minecraft" tile, from the same artboard: an isometric cube — the top face and the vertical seam. */
  cube: {
    d: ['M12 3l8 4.5v9L12 21l-8-4.5v-9z', 'M4 7.5l8 4.5 8-4.5', 'M12 12v9'],
  },
  /** The child tab bar's Quest mark — board C04 `:1044`, C11 `:1182`: a kite with a tail. */
  kite: {
    d: ['M12 2 19 8.5 12 17 5 8.5z', 'M5 8.5h14', 'M12 17c-1.5 1.5 1.5 3 0 5'],
  },
  apple: {
    fill: true,
    d: 'M16.37 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.5-1-2.5-3.9zM14.1 5.9c.6-.8 1.1-1.8 1-2.9-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.7-1 2.8 1 .1 2.1-.5 2.7-1.3z',
  },
  google: {
    fill: true,
    d: [
      'M21.8 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.5c-.2 1.3-1 2.4-2 3.1v2.6h3.3c1.9-1.8 3-4.4 3-7.5z',
      'M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.6c-.9.6-2.1 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3v2.7C4.7 19.9 8.1 22 12 22z',
      'M6.4 13.9c-.2-.6-.3-1.2-.3-1.9s.1-1.3.3-1.9V7.4H3C2.4 8.8 2 10.4 2 12s.4 3.2 1 4.6l3.4-2.7z',
      'M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9C17 2.9 14.7 2 12 2 8.1 2 4.7 4.1 3 7.4l3.4 2.7C7.2 7.8 9.4 6 12 6z',
    ],
    fills: brand.google,
  },

  // ---- Quest Jr. adventure badges ------------------------------------------------------------------------
  /*
   * The seven adventure glyphs, drawn by Evan on the 24 grid at stroke 2, round caps, no fill, ~0.85 of the
   * frame, and checked on a magenta disc at 88px, a dashed grey disc and a 36px disc (2026-09-18).
   *
   * **They are paths and not pictures, which is the whole reason they exist.** The commissioned set arrived as
   * 1024px raster, and a raster badge cannot be tinted — so it needs a second grey file per subject, and five
   * of the seven never had one. A path takes the disc's colour, so every badge has both colourways for free:
   * white on magenta when earned, grey on white when not, and nothing here knows which.
   *
   * Two subjects changed from the commission, for legibility at 36px. Being Trusted is a KEY rather than a
   * handshake, which reduces to crossed sticks — and you are GIVEN the key, which is the idea a credit product
   * wants. Borrowing and Earning is a COIN WITH A RETURN ARROW rather than two hands round a coin, which
   * reduces to a blob — and the arrow teaches the returning rather than only the taking. `ICON_ALIAS` below
   * points the retired names at these.
   *
   *   1 Earning It          broom          5 Where Money Lives      bank
   *   2 Spend It or Save It  signpost      6 Borrowing and Earning  coin-return
   *   3 Being Trusted        key           7 Making Money Grow      seedling-pot
   *   4 Keeping It Safe      shield
   */
  broom: {
    d: ['M10.5 8.5 15.5 13.5 11 21 3 13z', 'M21.5 2.5 7.5 16.5'],
  },
  signpost: {
    d: 'M8.4 2.6v18.8M8.4 4.6h11.4l1.8 2.8-1.8 2.8H8.4M8.4 13h-5l-1.8 2.8L3.4 18.6h5',
  },
  key: {
    d: ['M7.5 21a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z', 'M10.7 13.3 20 4l2 2', 'M16.5 7.5l2 2'],
  },
  shield: {
    d: 'M12 2.4 3.2 5.6v6.2c0 5 3.6 8.2 8.8 9.8 5.2-1.6 8.8-4.8 8.8-9.8V5.6zM8.4 12.2l2.6 2.6 5-5',
  },
  bank: {
    d: ['M2.5 8.5 12 2.5l9.5 6z', 'M6 11v7h6v-7v7h6v-7', 'M2.5 21.5h19'],
  },
  'coin-return': {
    d: [
      'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z',
      'M2.5 12a9.5 9.5 0 0 1 16.22-6.72v-3.5v3.5h-3.5',
      'M21.5 12a9.5 9.5 0 0 1-16.22 6.72v3.5v-3.5h3.5',
    ],
  },
  /** 05-2's "How?" pill (New Build board): a question mark in a circle, the circle a path as `alert`'s is. */
  question: {
    d: [
      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
      'M9.6 9.4a2.5 2.5 0 0 1 4.86.83c0 1.67-2.46 2.27-2.46 3.27',
      'M12 16.6h.01',
    ],
  },
  /** 05a's Oops row (New Build board): a face with a frown. The two eyes are one path, so it stays at three. */
  sad: {
    d: [
      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
      'M9 9.5h.01M15 9.5h.01',
      'M8.5 16a4.2 4.2 0 0 1 7 0',
    ],
  },
  'seedling-pot': {
    d: [
      'M5 14h14l-1.5 8h-11z',
      'M12 14V8c0-3-2.5-5-6-5 0 3.5 2.5 5 6 5 0-3 2.5-5 6-5 0 3.5-2.5 5-6 5',
    ],
  },

  // ---- Quest Jr. cubs ------------------------------------------------------------------------------------
  /*
   * Kai and Pip, drawn by Evan on the same spec as the badges (2026-09-18). They are the whole cast: content is
   * written to these two rather than the two being grown to content.
   *
   * **NEITHER HAS A FACE, and that is the drawing rather than an unfinished one.** Eyes and a mouth at stroke 2
   * on a 24 grid turn to mush by 44px — a first attempt made Pip read as a child in a mask. The difference is
   * carried by silhouette instead: Kai is taller, small head, brimmed cap; Pip has a bigger head, shorter body
   * and a top-knot. Checked at 96, 60, 44 and 36, and on a magenta badge disc.
   *
   * It also leaves the story free. A fixed expression would be wrong in most missions — one character has to be
   * pleased, worried and surprised across a single adventure — so a neutral figure lets the copy carry it.
   */
  kai: {
    d: 'M12 5.2a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4M7.6 21.6v-5a4.4 4.4 0 0 1 8.8 0v5M9 5.4a3 3 0 0 1 6 0M8.2 5.4h8.4',
  },
  pip: {
    d: 'M12 5a4.2 4.2 0 1 0 0 8.4A4.2 4.2 0 0 0 12 5M8 21.6v-3.2a4 4 0 0 1 8 0v3.2M12 5V2.4M10.8 3.2c.8-1 2.4-1 3 .4',
  },

  /**
   * **THE EIGHT CHORE PICTURES (P12b, 24 Sep)**, lifted from the artboard's own markup rather than drawn — the
   * rule this file's header exists for: *"a glyph absent from this file is a glyph nobody has lifted yet."*
   *
   * `bed` and `bowl` are the two C09's chore tiles already draw, and `pencil` is the same path P23 and P24
   * draw as their edit control — one subject, one drawing, one key.
   */
  bed: { d: ['M2 4v16', 'M2 8h18a2 2 0 0 1 2 2v10', 'M2 17h20', 'M6 8v9'] },
  bowl: { d: ['M3 11h18a9 9 0 0 1-18 0z', 'M9 7.5c0-1.4 1.3-2.5 3-2.5s3 1.1 3 2.5'] },
  backpack: {
    d: [
      'M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      'M8 10h8',
      'M8 18h8',
      'M8 22v-6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6',
      'M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
    ],
  },
  book: {
    d: [
      'M12 7v14',
      'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
    ],
  },
  pencil: {
    d: [
      'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
      'm15 5 4 4',
    ],
  },
  toothbrush: {
    d: [
      'M3 15h10',
      'M13 13h7a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-7z',
      'M14.5 13V9',
      'M17 13V8',
      'M19.5 13V9',
    ],
  },
  trash: {
    d: [
      'M3 6h18',
      'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
      'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
      'M10 11v6',
      'M14 11v6',
    ],
  },
  shirt: {
    d: [
      'M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z',
    ],
  },

  /*
   * **BRIEF 03's CATEGORY ICONS — the board's own paths, not my drawings** (05/03 on the new build's board).
   * Each `<circle>` is written as a path in the style `alert` and `clock` use, because this set has no circle
   * primitive. Household and Learning are not here: they are `home` and `book`, which already draw a house and
   * an open book. `CHORE_KIND_GLYPH` maps the six categories onto the six drawings.
   */
  /** Self-care: a head and shoulders. Board: `<circle cx=12 cy=8 r=4/><path d="M4 21a8 8 0 0 1 16 0"/>`. */
  person: { d: ['M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8z', 'M4 21a8 8 0 0 1 16 0'] },
  /** Outdoor: the sun. Board: `<circle cx=12 cy=12 r=4/>` and eight rays. */
  sun: {
    d: [
      'M12 16a4 4 0 1 0 0-8a4 4 0 0 0 0 8z',
      'M12 2v2',
      'M12 20v2',
      'M4.9 4.9l1.4 1.4',
      'M17.7 17.7l1.4 1.4',
      'M2 12h2',
      'M20 12h2',
      'M4.9 19.1l1.4-1.4',
      'M17.7 6.3l1.4-1.4',
    ],
  },
  /** Pets & plants: a paw — the pad and four toes. */
  paw: {
    d: [
      'M12 12c-3 0-5.5 3.2-5.5 5.5 0 1.7 1.3 2.5 2.7 2.5 1.2 0 1.8-.6 2.8-.6s1.6.6 2.8.6c1.4 0 2.7-.8 2.7-2.5 0-2.3-2.5-5.5-5.5-5.5z',
      'M8 8.5a2 2 0 1 0 0-4a2 2 0 0 0 0 4z',
      'M16 8.5a2 2 0 1 0 0-4a2 2 0 0 0 0 4z',
      'M4.5 13.5a2 2 0 1 0 0-4a2 2 0 0 0 0 4z',
      'M19.5 13.5a2 2 0 1 0 0-4a2 2 0 0 0 0 4z',
    ],
  },
  /** Helping others: a heart. */
  heart: { d: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z' },
} as const satisfies Record<GlyphKey, Glyph>;

/**
 * The names this build can draw. **Bound to `@kk/domain`'s `GLYPH_KEYS`**, which is the one list the API
 * validates authored content against and the O06 picker offers — so a drawing without a key, or a key
 * without a drawing, is a type error here rather than a tile that silently renders as a word.
 */
export type IconName = GlyphKey;

/**
 * **RETIRED SUBJECT NAMES, pointed at the glyph that replaced them.**
 *
 * `handshake` and `hands-coin` were commissioned for Being Trusted and Borrowing and Earning and then dropped:
 * both reduce to mush at 36px, and `key` and `coin-return` say the same things better (see the badge block
 * above). They are deliberately NOT registry keys — there is one drawing per subject and no duplicates — but
 * anything already naming them resolves here rather than falling back to nothing.
 *
 * This is the only alias layer. A per-feature second one would be two lists that can disagree, which is the
 * lesson of OPEN_QUESTIONS 111(e).
 */
export const ICON_ALIAS: Readonly<Record<string, IconName>> = {
  handshake: 'key',
  'hands-coin': 'coin-return',
};

/** The glyph a name resolves to — its own, or the one that replaced it — or `null` when this build has neither. */
export function resolveIconName(name: string): IconName | null {
  if (isIconName(name)) return name;
  return ICON_ALIAS[name] ?? null;
}

/**
 * **IS THIS A GLYPH THIS BUILD CAN ACTUALLY DRAW?**
 *
 * `IconName` is a compile-time union over the set above, and that is a promise about THIS build, not about the
 * wire. A server can name a glyph that was commissioned after the app shipped — the Quest Jr. badge art is
 * exactly that case, seven adventure glyphs arriving against a client whose `BadgeIconName` knows four — and a
 * type cannot stop a string arriving. What it can do is make the caller ask.
 *
 * So this is the runtime half of the type: it answers the question an `IconName` annotation only assumes, and
 * callers at the wire boundary use it to fall back rather than drawing an empty disc.
 */
export function isIconName(name: string): name is IconName {
  return Object.prototype.hasOwnProperty.call(glyphs, name);
}
